import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Church, ExternalLink, Heart, Facebook, Youtube, Instagram, Loader2, Send, Check } from "lucide-react";
import { useBrand } from "@/context/BrandContext";
import api from "@/lib/api";
import { toast } from "sonner";

const MINISTRY = "https://ihimm.org";

function Newsletter() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setState("loading");
    try {
      const { data } = await api.post("/newsletter", { email: email.trim() });
      setState("done");
      toast.success(data.already ? "You're already on the list 🙌" : "You're subscribed! Watch your inbox.");
    } catch (err) {
      setState("idle");
      toast.error(err?.response?.data?.detail || "Could not subscribe");
    }
  };
  if (state === "done") {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-300" data-testid="newsletter-success">
        <Check className="w-4 h-4" /> Thanks for subscribing!
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="flex items-center gap-2" data-testid="newsletter-form">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        data-testid="newsletter-email"
        className="flex-1 min-w-0 px-3 py-2.5 rounded-xl bg-[#0A1030] border border-white/10 text-sm text-white placeholder-zinc-600 focus:border-[#E6B34A] focus:outline-none"
      />
      <button
        type="submit"
        disabled={state === "loading"}
        data-testid="newsletter-submit"
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 shrink-0"
      >
        {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
      </button>
    </form>
  );
}

// Site footer linking back to the parent ministry, giving, and socials.
export default function Footer() {
  const brand = useBrand();
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-white/5 bg-[#0C1338] px-4 sm:px-6 lg:px-12 py-10" data-testid="site-footer">
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img src={brand.logo} alt="In His Image Radio" className="w-10 h-10 object-contain" />
            <div>
              <p className="font-display font-extrabold text-base leading-none">In His Image Radio</p>
              <p className="overline text-[#E6B34A] mt-1">Gospel · Praise · Worship</p>
            </div>
          </div>
          <p className="text-sm text-zinc-500 mt-4 leading-relaxed max-w-sm">A ministry of In HIS Image Movement Ministries — sharing the good news on the airwaves, anytime.</p>
          <div className="mt-5">
            <p className="overline text-zinc-400 mb-2">Get the newsletter</p>
            <p className="text-xs text-zinc-500 mb-3">Devotionals, show news & event alerts — no spam.</p>
            <Newsletter />
          </div>
        </div>

        <div>
          <p className="overline text-zinc-400 mb-3">Ministry</p>
          <ul className="space-y-2 text-sm">
            <li>
              <a href={MINISTRY} target="_blank" rel="noopener noreferrer" data-testid="footer-ministry" className="inline-flex items-center gap-2 text-zinc-300 hover:text-[#E6B34A] transition-colors">
                <Church className="w-4 h-4" /> In HIS Image Movement Ministries <ExternalLink className="w-3 h-3 opacity-60" />
              </a>
            </li>
            <li>
              <a href={`${MINISTRY}/give`} target="_blank" rel="noopener noreferrer" data-testid="footer-give" className="inline-flex items-center gap-2 text-zinc-300 hover:text-[#E6B34A] transition-colors">
                <Heart className="w-4 h-4" /> Give / Donate <ExternalLink className="w-3 h-3 opacity-60" />
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p className="overline text-zinc-400 mb-3">Follow</p>
          <div className="flex items-center gap-3">
            {[[Facebook, "facebook", `${MINISTRY}`], [Youtube, "youtube", `${MINISTRY}`], [Instagram, "instagram", `${MINISTRY}`]].map(([Icon, key, href]) => (
              <a key={key} href={href} target="_blank" rel="noopener noreferrer" data-testid={`footer-${key}`} className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#E6B34A]/20 hover:text-[#E6B34A] text-zinc-300 flex items-center justify-center transition-colors">
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto mt-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-zinc-600">© {year} In His Image Radio · In HIS Image Movement Ministries. All rights reserved.</p>
        <div className="flex items-center gap-4 text-xs">
          <Link to="/terms" data-testid="footer-terms" className="text-zinc-500 hover:text-[#E6B34A] transition-colors">Terms &amp; Conditions</Link>
          <Link to="/privacy" data-testid="footer-privacy" className="text-zinc-500 hover:text-[#E6B34A] transition-colors">Privacy Policy</Link>
        </div>
      </div>
    </footer>
  );
}
